import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import {
    LayoutDashboard, FolderOpen, Image as ImageIcon, Video, MessageSquare, Users, Quote,
    Settings as SettingsIcon, Search as SearchIcon, BarChart3, Instagram, LogOut, Moon, Sun, Menu, X, Lock,
    ExternalLink,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import ProtectedRoute from "./ProtectedRoute";

const nav = [
    { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", roles: ["super_admin", "editor"] },
    { to: "/admin/projects", icon: FolderOpen, label: "Projects", roles: ["super_admin", "editor"] },
    { to: "/admin/gallery", icon: ImageIcon, label: "Gallery", roles: ["super_admin", "editor"] },
    { to: "/admin/videos", icon: Video, label: "Videos", roles: ["super_admin", "editor"] },
    { to: "/admin/inquiries", icon: MessageSquare, label: "Inquiries", roles: ["super_admin", "editor"] },
    { to: "/admin/testimonials", icon: Quote, label: "Testimonials", roles: ["super_admin", "editor"] },
    { to: "/admin/instagram", icon: Instagram, label: "Instagram", roles: ["super_admin", "editor"] },
    { to: "/admin/users", icon: Users, label: "Users", roles: ["super_admin"] },
    { to: "/admin/settings", icon: SettingsIcon, label: "Settings", roles: ["super_admin", "editor"] },
    { to: "/admin/seo", icon: SearchIcon, label: "SEO", roles: ["super_admin", "editor"] },
    { to: "/admin/analytics", icon: BarChart3, label: "Analytics", roles: ["super_admin", "editor"] },
];

export default function AdminLayout() {
    return (
        <ProtectedRoute>
            <Shell />
        </ProtectedRoute>
    );
}

function Shell() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const visibleNav = nav.filter((n) => n.roles.includes(user?.role));

    return (
        <div className="min-h-screen bg-cream dark:bg-[#0e0c0a] text-ink dark:text-stone-100">
            {/* Mobile top bar */}
            <div className="md:hidden flex items-center justify-between border-b border-stone-border dark:border-stone-800 px-4 h-14 sticky top-0 z-30 bg-cream/95 dark:bg-[#0e0c0a]/95 backdrop-blur">
                <div className="font-serif text-lg">Admin</div>
                <button onClick={() => setOpen(!open)} data-testid="admin-mobile-toggle">{open ? <X size={20} /> : <Menu size={20} />}</button>
            </div>

            <div className="flex">
                {/* Sidebar */}
                <aside
                    data-testid="admin-sidebar"
                    className={`${open ? "block" : "hidden"} md:block fixed md:sticky top-0 inset-x-0 md:inset-auto md:left-0 md:top-0 z-20 md:h-screen w-full md:w-64 bg-white dark:bg-[#171311] border-r border-stone-border dark:border-stone-800 md:flex md:flex-col`}
                >
                    <div className="px-6 py-6 border-b border-stone-border dark:border-stone-800 hidden md:block">
                        <Link to="/" className="font-serif text-xl">Jassi Wood Works</Link>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-walnut-light mt-1">Admin Panel</div>
                    </div>

                    <nav className="p-3 space-y-0.5 md:flex-1 overflow-y-auto">
                        {visibleNav.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                onClick={() => setOpen(false)}
                                data-testid={`admin-nav-${n.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded text-sm transition-colors ${
                                        isActive
                                            ? "bg-walnut text-cream"
                                            : "text-stone-700 dark:text-stone-300 hover:bg-sand dark:hover:bg-stone-800"
                                    }`
                                }
                            >
                                <n.icon size={16} /> {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="p-3 border-t border-stone-border dark:border-stone-800 space-y-2">
                        <div className="px-3 py-2 text-xs text-stone-muted dark:text-stone-400">
                            <div className="truncate font-medium text-ink dark:text-stone-100" data-testid="admin-current-user">{user?.name}</div>
                            <div className="truncate">{user?.email}</div>
                            <div className="mt-1 inline-block bg-walnut-light text-cream px-2 py-0.5 text-[10px] uppercase tracking-widest">{user?.role}</div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                data-testid="admin-theme-toggle"
                                onClick={toggle}
                                className="flex-1 inline-flex items-center justify-center gap-2 border border-stone-border dark:border-stone-700 px-3 py-2 text-xs hover:bg-sand dark:hover:bg-stone-800"
                            >
                                {theme === "dark" ? <><Sun size={14} /> Light</> : <><Moon size={14} /> Dark</>}
                            </button>
                            <NavLink
                                to="/admin/change-password"
                                onClick={() => setOpen(false)}
                                data-testid="admin-change-password-link"
                                className="flex-1 inline-flex items-center justify-center gap-2 border border-stone-border dark:border-stone-700 px-3 py-2 text-xs hover:bg-sand dark:hover:bg-stone-800"
                            >
                                <Lock size={14} /> Password
                            </NavLink>
                        </div>
                        <a href="/" target="_blank" rel="noreferrer" className="block text-xs text-stone-muted hover:text-walnut inline-flex items-center gap-1 px-3">
                            <ExternalLink size={12} /> View website
                        </a>
                        <button
                            data-testid="admin-logout"
                            onClick={async () => {
                                await logout();
                                localStorage.removeItem("jassi_token");
                                navigate("/admin/login", { replace: true });
                                // Hard reload safety net in case route guard caches state
                                setTimeout(() => {
                                    if (window.location.pathname.startsWith("/admin") && window.location.pathname !== "/admin/login") {
                                        window.location.href = "/admin/login";
                                    }
                                }, 50);
                            }}
                            className="w-full inline-flex items-center justify-center gap-2 bg-walnut text-cream px-3 py-2 text-xs hover:bg-walnut-dark"
                        >
                            <LogOut size={14} /> Sign out
                        </button>
                    </div>
                </aside>

                {/* Main */}
                <main className="flex-1 min-w-0 p-5 md:p-10">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
